import React from 'react';
import styled from 'styled-components';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import { AnimatedSection } from '../../components/common/AnimatedSection';
import { useNavigate } from 'react-router-dom';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #ffffff;
  padding-top: 10%;
  @media (max-width: 768px) {
    padding-top: 30%;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 3rem;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  padding: 3rem 0;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111111;
  margin-bottom: 1.5rem;
  letter-spacing: -1px;
`;

const PageSubtitle = styled.p`
  font-size: 1.1rem;
  color: #6b7280;
  @media (max-width: 768px) {
    font-size: 0.9rem;
    white-space: pre-line;
  }
`;

const Table = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
`;

const TableRow = styled.div`
  display: flex;
  border-top: 1px solid #eee;

  &:first-child {
    border-top: none;
  }

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const TableTitle = styled.div`
  flex: 1;
  background: #f9fafb;
  padding: 1.5rem;
  font-weight: 600;
  color: #111;
  white-space: nowrap;

  @media (max-width: 768px) {
    border-bottom: 1px solid #eee;
  }
`;

const TableContent = styled.div`
  flex: 3;
  padding: 1.5rem;
  font-size: 0.95rem;
  color: #4b5563;
  line-height: 1.7;
  white-space: pre-line;
`;

const ActionSection = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin: 4rem 0;
  flex-wrap: wrap;
`;

const ActionCard = styled.div`
  flex: 1 1 300px;
  padding: 3rem;
  background: #f9fafb;
  border-radius: 0.75rem;
  text-align: center;
  box-shadow: 0 4px 10px rgba(0,0,0,0.03);
`;

const ActionTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const ActionText = styled.p`
  font-size: 0.95rem;
  color: #6b7280;
  margin-bottom: 1rem;
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background-color: #e5e7eb;
  margin-bottom: 2rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  @media (max-width: 768px) {
    gap: 1.2rem;
  }
`;

const ActionButton = styled.button`
  padding: 0.7rem 1.5rem;
  border: none;
  border-radius: 9999px;
  background-color: #3b82f6;
  color: white;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;

  &:hover {
    background-color: #2563eb;
  }

  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.7rem 1rem;
  }
  @media (max-width: 480px) {
    font-size: 0.6rem;
    padding: 0.7rem 1rem;
  }
`;

const StepSection = styled.div`
  margin-bottom: 6rem;
`;

const StepTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #111;
`;

const StepList = styled.div`
  font-size: 0.95rem;
  color: #4b5563;
  line-height: 1.7;
  white-space: pre-line;
`;

function Report() {
  const [headerRef, headerVisible] = useScrollAnimation();
  const [tableRef, tableVisible] = useScrollAnimation();
  const navigate = useNavigate();

  return (
    <PageContainer>
      <Container>
        <AnimatedSection ref={headerRef} isVisible={headerVisible} animation="fadeInUp">
          <PageHeader>
            <PageTitle>제보센터</PageTitle>
            <PageSubtitle>
              {"세연이앤에스는 윤리적 경영과 투명한 문화를 위해 \n누구나 쉽게 제보할 수 있도록 제보센터를 운영하고 있습니다."}
            </PageSubtitle>
          </PageHeader>
        </AnimatedSection>

        <AnimatedSection ref={tableRef} isVisible={tableVisible} animation="fadeInUp" delay="0.2s">
          <Table>
            <TableRow>
              <TableTitle>제보대상유형</TableTitle>
              <TableContent>
                · 윤리강령 및 법규 위반{"\n"}
                · 금품요구 및 부당요구{"\n"}
                · 갑질 및 부정거래{"\n"}
                · 직장 내 괴롭힘 및 인권 침해{"\n"}
                · 회사 자산 부당사용{"\n"}
                · 기타 법 위반 사항 등
              </TableContent>
            </TableRow>
            <TableRow>
              <TableTitle>제보자 보호 원칙</TableTitle>
              <TableContent>
                · 실명 또는 익명 가능, 불이익 금지{"\n"}
                · 조사내용과 정보는 비밀보장{"\n"}
                · 제보자 보호를 위한 모든 조치{"\n"}
                · 위법행위 자진 신고 시 감면 가능
              </TableContent>
            </TableRow>
          </Table>
        </AnimatedSection>

        <AnimatedSection isVisible={tableVisible} animation="fadeInUp" delay="0.4s">
          <ActionSection>
            <ActionCard>
                <i className="fi fi-rr-megaphone" style={{ fontSize: '2rem', color: '#3b82f6', marginBottom: '1rem' }}></i>
              <ActionTitle>제보하기</ActionTitle>
              <Divider />
              <ActionText>실명 또는 익명으로 제보 가능합니다.</ActionText>
              <ButtonGroup>
                <ActionButton onClick={() => navigate('/report/real')}>실명으로 제보하기</ActionButton>
                <ActionButton onClick={() => navigate('/report/anonymous')}>익명으로 제보하기</ActionButton>
              </ButtonGroup>
            </ActionCard>

            <ActionCard>
                <i className="fi fi-rr-document-signed" style={{ fontSize: '2rem', color: '#3b82f6', marginBottom: '1rem' }}></i>
              <ActionTitle>익명제보 조치내용 확인</ActionTitle>
              <Divider />
              <ActionText>입력한 아이디와 비밀번호로 확인하세요.</ActionText>
              <ButtonGroup>
                <ActionButton onClick={() => navigate('/report/check')}>조치내용 확인하기</ActionButton>
              </ButtonGroup>
            </ActionCard>
          </ActionSection>
        </AnimatedSection>

        <AnimatedSection isVisible={tableVisible} animation="fadeInUp" delay="0.6s">
          <StepSection>
            <StepTitle>처리 절차</StepTitle>
                <StepList>
                1. 제보 접수{"\n"}
                제보자는 실명 또는 익명으로 제보를 등록할 수 있으며, 등록 후 제보 접수 안내 메일이 발송됩니다.{"\n\n"}

                2. 제보 확인{"\n"}
                제보센터 담당자가 제보 내용을 확인하여 조사 필요성을 판단합니다.{"\n"}
                - 조사 필요 없음: 조사불요 의견 등록 및 안내 메일 발송{"\n"}
                - 조사 필요 있음: 조사 진행 등록 및 안내 메일 발송{"\n\n"}

                3. 사실 확인 조사{"\n"}
                조사 필요성이 인정된 경우, 객관적인 근거 확보 및 조사를 통해 사실 여부를 확인합니다.{"\n"}
                필요 시 관련자 대상 조사 및 추가 확인 절차가 진행됩니다.{"\n\n"}

                4. 종결 및 처리결과 확인{"\n"}
                내부 규정에 따라 조치를 완료하며, 결과는 다음 방식으로 확인할 수 있습니다.{"\n"}
                - 실명 제보자: 등록한 이메일로 조치내역 안내{"\n"}
                - 익명 제보자: 익명 제보 확인 페이지에서 확인 가능
                </StepList>
          </StepSection>
        </AnimatedSection>
      </Container>
    </PageContainer>
  );
}

export default Report;
